/**
 * Schémas Zod du module Vins clairs (Lot C.3) — partagés API ⇄ ERP.
 *
 * Le VIN CLAIR est le vin tranquille issu du pressurage + fermentations (FA/FML),
 * AVANT assemblage/tirage. Entité distincte du moût (`pressurage_fractions`) et de
 * la bouteille. Il vit dans une cuve, dont il peut changer (transfert), et son
 * volume ne diminue que lorsqu'une perte réelle est constatée. Cf table `vins_clairs`
 * (migration 0041) et `docs/features/lot-c-vinification/01-cadrage-metier.md` § C.3.
 *
 * Source de vérité des énumérations : les CHECK SQL des tables `vins_clairs` et
 * `mouvements_vin_clair` (alignement strict — ne jamais diverger de la BDD).
 *   - vins_clairs.statut      ∈ ('fa_en_cours','fa_terminee','fml_en_cours','fml_terminee','stabilise')
 *   - vins_clairs.fml_statut  ∈ ('non_demarree','en_cours','faite','bloquee')
 *   - vins_clairs.fraction    ∈ ('cuvee','taille','rebeche')
 *   - mouvements.type         ∈ ('transfert','soutirage','filtration','debourbage',
 *                                'collage','ouillage','ajout_so2','perte',
 *                                'assemblage_entrant','mise_en_reserve','retour_reserve',
 *                                'ajustement','annulation')
 *
 * Conventions métier :
 *  - Volumes en LITRES (`volume_actuel_l`, `volume_l`), NUMERIC → string en sortie.
 *  - Le volume actuel d'un vin clair n'est JAMAIS saisi : il se reconstitue par
 *    somme des `mouvements_vin_clair` (append-only, source de vérité). Le cache
 *    `volume_actuel_l` est maintenu par l'application en transaction.
 *  - FML jamais obligatoire dans le flux (choix du vigneron).
 */

import { z } from "zod";

// `millesimeSchema` (+ bornes) et `dateIsoSchema` sont déjà définis pour les Lots
// C.1/C.2 — on les RÉUTILISE (DRY : une seule source de vérité pour ces règles
// transverses), on ne les redéclare pas (collision de barrel sinon).
import { millesimeSchema } from "./campagnes.js";
import {
  contexteAffectationReserveSchema,
  contexteCommandeLiquideSchema,
  versionStockLiquideSchema,
} from "./liquides.js";
import { idSchema } from "./parcellaire.js";
import { dateIsoSchema } from "./vendange.js";

// ────────────────────────────────────────────────────────────────────────────
// Bornes métier — volumes
// ────────────────────────────────────────────────────────────────────────────

/** Volume minimal d'un mouvement de cave (garde-fou saisie). 0,01 L = précision NUMERIC(10,2). */
export const VOLUME_L_MIN = 0.01;
/**
 * Volume maximal accepté (garde-fou saisie) : 1 000 000 L (1 000 hL). Aucune
 * cuve de cave champenoise n'atteint ce volume — réajustable si besoin réel.
 */
export const VOLUME_L_MAX = 1_000_000;

// ────────────────────────────────────────────────────────────────────────────
// Énumérations (miroir des CHECK SQL)
// ────────────────────────────────────────────────────────────────────────────

/** Statut œnologique (cycle principal) — miroir CHECK SQL `vins_clairs.statut`. */
export const vinClairStatutSchema = z.enum([
  "fa_en_cours",
  "fa_terminee",
  "fml_en_cours",
  "fml_terminee",
  "stabilise",
]);
export type VinClairStatut = z.infer<typeof vinClairStatutSchema>;

/**
 * Statut de la FML (malolactique), OPTIONNELLE en Champagne — miroir CHECK SQL
 * `vins_clairs.fml_statut`. `bloquee` = volontairement non déclenchée (vivacité).
 */
export const fmlStatutSchema = z.enum(["non_demarree", "en_cours", "faite", "bloquee"]);
export type FmlStatut = z.infer<typeof fmlStatutSchema>;

/** Fraction d'origine prédominante — miroir CHECK SQL `vins_clairs.fraction`. */
export const fractionSchema = z.enum(["cuvee", "taille", "rebeche"]);
export type Fraction = z.infer<typeof fractionSchema>;

/**
 * Type de mouvement de cave — miroir CHECK SQL `mouvements_vin_clair.type`.
 *
 * Les types EXPOSÉS À LA SAISIE manuelle (cf cadrage C.3 « Mouvements de cuve »)
 * sont restreints volontairement : les types système (`mise_en_reserve`,
 * `retour_reserve`, `assemblage_entrant`, `annulation`) sont créés par d'autres
 * flux (conversion réserve, Lot D, annulation) et ne doivent pas être saisis
 * directement par l'utilisateur.
 */
export const mouvementTypeSaisissableSchema = z.enum([
  "transfert",
  "soutirage",
  "filtration",
  "debourbage",
  "perte",
  "ajustement",
]);
export type MouvementTypeSaisissable = z.infer<typeof mouvementTypeSaisissableSchema>;

/**
 * Domaine COMPLET des types de mouvement (saisissables + système). Utilisé en
 * SORTIE uniquement (une ligne lue peut porter un type système). En entrée, on
 * n'accepte que `mouvementTypeSaisissableSchema`.
 */
export const mouvementTypeSchema = z.enum([
  "transfert",
  "soutirage",
  "filtration",
  "debourbage",
  "collage",
  "ouillage",
  "ajout_so2",
  "perte",
  "assemblage_entrant",
  "mise_en_reserve",
  "retour_reserve",
  "enrichissement",
  "ajustement",
  "annulation",
]);
export type MouvementType = z.infer<typeof mouvementTypeSchema>;

/**
 * Gestes autonomes corrigeables depuis le registre des vins clairs (API + UI).
 * Liste fermée : un nouveau type système n'est jamais annulable implicitement,
 * ses contreparties et sa permission relèvent de l'opération complète.
 * Les gestes historiques `collage`, `ouillage` et `ajout_so2` restent admis.
 */
export const TYPES_MOUVEMENT_ANNULABLES_DIRECTEMENT = [
  "filtration",
  "debourbage",
  "collage",
  "ouillage",
  "ajout_so2",
  "perte",
  "ajustement",
] as const satisfies readonly MouvementType[];

/** Système de réserve (cible d'une conversion) — miroir CHECK SQL `vins_reserve.systeme`. */
export const systemeReserveSchema = z.enum(["perpetuelle", "par_annee"]);
export type SystemeReserve = z.infer<typeof systemeReserveSchema>;

/** Couleur constatée du lot. `inconnu` est réservé aux historiques à qualifier. */
export const couleurVinClairSchema = z.enum(["blanc", "rouge", "rose", "inconnu"]);
export type CouleurVinClair = z.infer<typeof couleurVinClairSchema>;

/** Méthode rosé explicite : elle ne se déduit jamais du cépage. */
export const methodeRoseSchema = z.enum([
  "pressurage_direct",
  "maceration",
  "saignee",
  "assemblage",
  "inconnue",
]);
export type MethodeRose = z.infer<typeof methodeRoseSchema>;

export const qualificationCouleurSchema = z.enum(["qualifie", "a_qualifier"]);
export type QualificationCouleur = z.infer<typeof qualificationCouleurSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques
// ────────────────────────────────────────────────────────────────────────────

/** Code INSEE commune (5 chars), optionnel — vide normalisé en undefined. */
const cruCommuneInseeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(5, "Code commune INSEE invalide (5 caractères).")
  .optional()
  .or(z.literal("").transform(() => undefined));

/** Volume en litres strictement positif et borné (garde-fou de saisie). */
export const volumeLitresSchema = z
  .number({
    invalid_type_error: "Volume invalide.",
    required_error: "Volume requis.",
  })
  .positive("Le volume doit être supérieur à 0.")
  .min(VOLUME_L_MIN, `Volume minimum : ${VOLUME_L_MIN} L.`)
  .max(VOLUME_L_MAX, `Volume maximum : ${VOLUME_L_MAX} L (1000 hL).`);

/** Texte libre court optionnel — vide normalisé en undefined. */
const optionalShortText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} trop long (${max} caractères max).`)
    .optional()
    .or(z.literal("").transform(() => undefined));

/** Notes libres (multi-ligne) optionnelles — vide normalisé en undefined. */
const notesSchema = z
  .string()
  .trim()
  .max(4000, "Notes trop longues (4000 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

// ────────────────────────────────────────────────────────────────────────────
// Input — création d'un vin clair
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `vinClair.creer` — créer un lot de vin clair.
 *
 * `volume_initial_l` = volume à la mise en cuve. Il N'EST PAS écrit tel quel dans
 * `volume_actuel_l` sans contrepartie : la création enregistre AUSSI un mouvement
 * initial `ajustement` (entrée) qui ancre le solde dans le registre append-only
 * (source de vérité). Le `cuve_id` est la localisation courante (optionnelle :
 * un vin en transit n'a pas de cuve).
 */
export const vinClairCreerInput = z.object({
  /** Fourni par le formulaire après lecture d'un suivi physique actif. */
  contexte_liquide: contexteCommandeLiquideSchema
    .pick({ uuid_commande: true, schema_version: true, version_bascule: true })
    .extend({ version_cuve_attendue: versionStockLiquideSchema.optional() })
    .optional(),
  /** Libellé/numéro de lot interne (libre). */
  libelle: optionalShortText(80, "Libellé"),
  /** Marc d'origine (optionnel — peut provenir d'un achat de moût). */
  marc_id: idSchema.optional(),
  /** Fraction d'origine prédominante (cuvée/taille/rebèche). */
  fraction: fractionSchema.optional(),
  /** Cépage du vin clair (mono avant assemblage). */
  cepage_id: idSchema.optional(),
  /** Cru/commune dominant (code INSEE). */
  cru_commune_insee: cruCommuneInseeSchema,
  /** Millésime (année de récolte d'origine). Obligatoire. */
  millesime: millesimeSchema,
  /** Volume initial à la mise en cuve, en litres. Obligatoire. */
  volume_initial_l: volumeLitresSchema,
  /** Cuve de stockage initiale (optionnelle : vin en transit sans cuve). */
  cuve_id: idSchema.optional(),
  /** Statut œnologique initial (défaut DB : `fa_en_cours`). */
  statut: vinClairStatutSchema.optional(),
  /** Statut FML initial (défaut DB : `non_demarree`). */
  fml_statut: fmlStatutSchema.optional(),
  /** Date de mise en cuve (ancre le mouvement initial). */
  date_mise_en_cuve: dateIsoSchema,
  /** Notes libres. */
  notes: notesSchema,
});
export type VinClairCreerInput = z.infer<typeof vinClairCreerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Input — mouvement de cave (append-only)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `vinClair.enregistrerMouvement` — ajouter un mouvement au registre.
 *
 * APPEND-ONLY : un mouvement enregistré ne se modifie jamais (correction par
 * `vinClair.annulerMouvement` = opération inverse).
 *
 * `volume_l` est le volume POSITIF concerné par le geste. Le serveur en déduit
 * séparément son impact matière : transfert/soutirage/filtration/débourbage ne
 * changent pas le stock ; seule une perte réellement mesurée le diminue.
 */
export const vinClairMouvementInput = z
  .object({
    /** Vin clair concerné. */
    vin_clair_id: idSchema,
    /** Type de mouvement (saisissable uniquement — pas de type système). */
    type: mouvementTypeSaisissableSchema,
    /** Volume du mouvement en litres, TOUJOURS positif (le sens vient du type). */
    volume_l: volumeLitresSchema,
    /**
     * Sens explicite — requis UNIQUEMENT pour `ajustement` (qui peut être une
     * entrée correctrice ou une sortie). Ignoré pour les autres types (sens
     * déterministe). `'entree'` = +volume, `'sortie'` = -volume.
     */
    sens: z.enum(["entree", "sortie"]).optional(),
    /** Cuve source (départ). Optionnelle (un débourbage peut rester en place). */
    cuve_source_id: idSchema.optional(),
    /** Cuve destination (arrivée). Requise sur un transfert (validée en invariant). */
    cuve_dest_id: idSchema.optional(),
    /** Date de l'opération. */
    date_mouvement: dateIsoSchema,
    /** Opérateur ayant réalisé/saisi le mouvement (référentiel utilisateur global). */
    operateur_id: idSchema.optional(),
    /** Notes libres. */
    notes: notesSchema,
  })
  .superRefine((value, ctx) => {
    // `sens` n'a de sens (sic) que pour un ajustement : pour les autres types le
    // signe est déterministe, fournir `sens` est une erreur de saisie ambiguë.
    if (value.sens !== undefined && value.type !== "ajustement") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sens"],
        message: "Le sens n'est paramétrable que pour un mouvement d'ajustement.",
      });
    }
    if (value.type === "ajustement" && value.sens === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sens"],
        message: "Indiquez si l'ajustement est une entrée ou une sortie.",
      });
    }
    // Transfert et soutirage déplacent le lot : la destination est obligatoire.
    if (
      (value.type === "transfert" || value.type === "soutirage") &&
      value.cuve_dest_id === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cuve_dest_id"],
        message: "Une cuve de destination est requise pour ce déplacement.",
      });
    }
    if (value.type === "perte" && !value.notes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["notes"],
        message: "Indiquez le motif de la perte réelle.",
      });
    }
  });
export type VinClairMouvementInput = z.infer<typeof vinClairMouvementInput>;

/**
 * Input `vinClair.annulerMouvement` — annuler un mouvement par OPÉRATION INVERSE.
 * Crée un nouveau mouvement `annulation` portant le volume opposé et pointant
 * sur l'original (`mouvement_annule_id`). N'édite ni ne supprime l'original.
 */
export const annulerMouvementInput = z.object({
  /** Mouvement à annuler. */
  mouvement_id: idSchema,
  /** Date de l'annulation (par défaut : aujourd'hui côté serveur si absente). */
  date_mouvement: dateIsoSchema.optional(),
  /** Motif libre de l'annulation. */
  motif: optionalShortText(120, "Motif"),
});
export type AnnulerMouvementInput = z.infer<typeof annulerMouvementInput>;

// ────────────────────────────────────────────────────────────────────────────
// Input — mise à jour du statut FML & du statut œnologique
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `vinClair.majFmlStatut` — faire évoluer le statut FML (jalon œnologique).
 *
 * FML jamais obligatoire (choix du vigneron) : on peut passer directement à
 * `bloquee`. Mise à jour d'un JALON (pas une valeur métier append-only), donc
 * UPDATE légitime ici. `statut_oenologique` optionnel pour synchroniser le cycle
 * principal en même temps (ex. FML faite → `fml_terminee`).
 */
export const majFmlStatutInput = z.object({
  /** Vin clair concerné. */
  vin_clair_id: idSchema,
  /** Nouveau statut FML. */
  fml_statut: fmlStatutSchema,
  /** Statut œnologique principal à poser conjointement (optionnel). */
  statut_oenologique: vinClairStatutSchema.optional(),
  /** Date du jalon (informative, tracée en notes). */
  date_jalon: dateIsoSchema.optional(),
});
export type MajFmlStatutInput = z.infer<typeof majFmlStatutInput>;

// ────────────────────────────────────────────────────────────────────────────
// Input — conversion en vin de réserve
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `vinClair.convertirEnReserve` — mettre tout ou partie d'un vin clair en
 * réserve (cf cadrage C.3 « Conversion en vin de réserve » + C.4 RQI).
 *
 * Effets (transaction unique) : enregistre un mouvement `mise_en_reserve`
 * (sortie) sur le vin clair, crée/alimente un lot `vins_reserve` + son mouvement
 * `apport`. Le vin clair doit être STABILISÉ (invariant métier). Le plafond de
 * réserve est un PARAMÈTRE annuel (jamais vérifié en dur ici).
 */
export const convertirReserveInput = z.object({
  /** Position physique à qualifier, requise après activation du journal liquide. */
  contexte_liquide: contexteAffectationReserveSchema.optional(),
  /** Vin clair source. */
  vin_clair_id: idSchema,
  /** Volume à mettre en réserve, en litres (≤ volume actuel — vérifié en invariant). */
  volume_l: volumeLitresSchema,
  /** Système de réserve cible (perpétuelle / par année). */
  systeme: systemeReserveSchema,
  /**
   * Lot de réserve existant à ALIMENTER (optionnel). Si absent, on CRÉE un lot.
   * Cas typique alimentation : réserve perpétuelle (solera).
   */
  reserve_id: idSchema.optional(),
  /** Date de la mise en réserve. */
  date_mouvement: dateIsoSchema,
  /** Notes/motif libre. */
  notes: notesSchema,
});
export type ConvertirReserveInput = z.infer<typeof convertirReserveInput>;

// ────────────────────────────────────────────────────────────────────────────
// Input — lecture
// ────────────────────────────────────────────────────────────────────────────

/** Input `vinClair.getById` — fiche détail + mouvements. */
export const vinClairGetByIdInput = z.object({ id: idSchema });
export type VinClairGetByIdInput = z.infer<typeof vinClairGetByIdInput>;

/** Input `vinClair.lister` — filtres optionnels pour la liste tenant. */
export const vinClairListerInput = z
  .object({
    cuve_id: idSchema.optional(),
    statut: vinClairStatutSchema.optional(),
    cepage_id: idSchema.optional(),
    millesime: millesimeSchema.optional(),
  })
  .optional();
export type VinClairListerInput = z.infer<typeof vinClairListerInput>;

/** Borne haute du nombre de mouvements renvoyés pour une cuve (garde-fou). */
export const MOUVEMENTS_CUVE_LIMITE_MAX = 100;

/**
 * Input `vinClair.listerMouvementsCuve` — mouvements récents d'UNE cuve
 * (registre de la fiche cuve). `limite` optionnelle (défaut serveur : 20).
 */
export const mouvementsCuveListerInput = z.object({
  cuve_id: idSchema,
  limite: z
    .number({ invalid_type_error: "Limite invalide." })
    .int("La limite doit être un entier.")
    .positive("La limite doit être supérieure à 0.")
    .max(MOUVEMENTS_CUVE_LIMITE_MAX, `Limite maximum : ${MOUVEMENTS_CUVE_LIMITE_MAX}.`)
    .optional(),
});
export type MouvementsCuveListerInput = z.infer<typeof mouvementsCuveListerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de SORTIE (validation Zod en sortie de tRPC — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Représentation d'un vin clair renvoyée par l'API. `volume_actuel_l` est une
 * chaîne (NUMERIC Drizzle → string) pour préserver la précision décimale.
 */
export const vinClairSortieSchema = z.object({
  id: idSchema,
  libelle: z.string().nullable(),
  marc_id: idSchema.nullable(),
  fraction: fractionSchema.nullable(),
  cepage_id: idSchema.nullable(),
  cru_commune_insee: z.string().nullable(),
  millesime: z.number().int(),
  volume_actuel_l: z.string(),
  cuve_id: idSchema.nullable(),
  statut: vinClairStatutSchema,
  fml_statut: fmlStatutSchema,
  couleur: couleurVinClairSchema,
  methode_rose: methodeRoseSchema.nullable(),
  qualification_couleur: qualificationCouleurSchema,
  notes: z.string().nullable(),
  cree_le: z.date(),
});
export type VinClairSortie = z.infer<typeof vinClairSortieSchema>;

/** Sortie `vinClair.lister` — liste de vins clairs. */
export const vinClairListeSortieSchema = z.array(vinClairSortieSchema);

/** Représentation d'un mouvement de vin clair (registre). */
export const mouvementVinClairSortieSchema = z.object({
  id: idSchema,
  vin_clair_id: idSchema,
  type: mouvementTypeSchema,
  volume_l: z.string(),
  semantique_volume_version: z.number().int(),
  volume_traite_l: z.string().nullable(),
  delta_stock_l: z.string().nullable(),
  cuve_source_id: idSchema.nullable(),
  cuve_dest_id: idSchema.nullable(),
  date_mouvement: z.string(),
  operateur_id: idSchema.nullable(),
  mouvement_annule_id: idSchema.nullable(),
  notes: z.string().nullable(),
  cree_le: z.date(),
});
export type MouvementVinClairSortie = z.infer<typeof mouvementVinClairSortieSchema>;

/** Sortie `vinClair.getById` — fiche détail + registre des mouvements. */
export const vinClairDetailSortieSchema = vinClairSortieSchema.extend({
  mouvements: z.array(mouvementVinClairSortieSchema),
});
export type VinClairDetailSortie = z.infer<typeof vinClairDetailSortieSchema>;

/**
 * Mouvement enrichi du vin clair porteur (libellé + millésime) pour le registre
 * de la FICHE CUVE : le vin clair peut avoir quitté la cuve, on garde son nom.
 */
export const mouvementCuveSortieSchema = mouvementVinClairSortieSchema.extend({
  vin_clair_libelle: z.string().nullable(),
  vin_clair_millesime: z.number().int(),
});
export type MouvementCuveSortie = z.infer<typeof mouvementCuveSortieSchema>;

/** Sortie `vinClair.listerMouvementsCuve` — mouvements récents (plus récent en tête). */
export const mouvementsCuveListeSortieSchema = z.array(mouvementCuveSortieSchema);

/** Sortie `vinClair.creer` — id + date de création. */
export const vinClairCreerSortieSchema = z.object({
  id: idSchema,
  cree_le: z.date(),
});

/** Sortie `vinClair.enregistrerMouvement` — id du mouvement + nouveau solde. */
export const enregistrerMouvementSortieSchema = z.object({
  mouvement_id: idSchema,
  volume_actuel_l: z.string(),
  cree_le: z.date(),
});

/** Sortie `vinClair.annulerMouvement` — mouvement inverse + nouveau solde. */
export const annulerMouvementSortieSchema = z.object({
  mouvement_id: idSchema,
  mouvement_annule_id: idSchema,
  volume_actuel_l: z.string(),
  cree_le: z.date(),
});

/** Sortie `vinClair.majFmlStatut` — id + nouveaux statuts. */
export const majFmlStatutSortieSchema = z.object({
  id: idSchema,
  fml_statut: fmlStatutSchema,
  statut: vinClairStatutSchema,
  updated_at: z.date(),
});

/** Sortie `vinClair.convertirEnReserve` — lot de réserve + nouveau solde du vin clair. */
export const convertirReserveSortieSchema = z.object({
  reserve_id: idSchema,
  mouvement_vin_clair_id: idSchema,
  mouvement_reserve_id: idSchema,
  volume_actuel_l: z.string(),
});
